import {Tween} from '@gongxigames/framework/core/fragments/tween/Tween';
import {UpdateManager} from '@gongxigames/framework/core/services/UpdateManager';
import {IAnimation} from '@gongxigames/framework/core/types';
import {Container} from 'pixi.js';
import {AUTOMATION_META, AutomationMeta} from '@gongxigames/framework/core/fragments/Autotest';

interface TweenTimeObject {
    tween: Tween;
    time: number;
}

export class Animation extends UpdateManager implements IAnimation {

    protected _loop = false;

    private _container: Container = new Container();
    private _tweens: Tween[] = [];
    private _onComplete?: Function;
    private _onTweenComplete?: Function;
    private _duration = 0;

    public start(loop = false): void {
        super.start();

        this._loop = loop;

        if (!this._tweens.length) {
            return;
        }

        for (const tween of this._tweens) {
            this.add(tween);
            tween.start();
        }

        const tween = this.findLatestTween();

        tween.onComplete(() => {
            this.onLatestTweenCompleted(tween);
        });
    }

    public stop(): void {
        super.stop();

        for (const tween of this._tweens) {
            tween.stop();
        }

        if (this._container.parent) {
            this._container.parent.removeChild(this._container);
        }
    }

    // TODO: refactoring required!
    // @ts-ignore
    public addTween(tween: Tween): void {
        // if (this.isLive) {
        //     throw new Error('You can add tween only when animation isn\'t playing');
        // }

        this.addTweenCallbacks(tween);

        this._tweens.push(tween);
    }

    public addTweens(tweens: Tween[]): void {
        for (const tween of tweens) {
            this.addTween(tween);
        }
    }

    /**
     * every tween calls this callback
     * @param callback
     */
    public setOnTweenComplete(callback: Function): void {
        this._onTweenComplete = callback;
    }

    public setOnComplete(callback: Function): void {
        this._onComplete = callback;
    }

    public getDuration(): number {
        return this._duration;
    }

    public get container(): Container {
        return this._container;
    }

    private findLatestTween(): Tween {
        const tweenObjects: TweenTimeObject[] = [];
        let latestTween;
        let maxTime = -1;

        for (const tween of this._tweens) {
            const loops = tween.getRepeat();
            let duration = tween.duration;

            if (loops > 0 && isFinite(loops)) {
                duration = loops * duration;
            }

            this.addTweenTimeObjects(tween, duration, tweenObjects);
        }

        for (const tweenObj of tweenObjects) {
            if (tweenObj.time > maxTime) {
                maxTime = tweenObj.time;
                latestTween = tweenObj.tween;
            }
        }

        this._duration = maxTime;

        return latestTween as Tween;
    }

    private addTweenTimeObjects(tween: Tween, time: number, tweenObjects: TweenTimeObject[]): void {
        if (tween.chainedTweens.length) {
            for (const chained of tween.chainedTweens) {
                const loops = chained.getRepeat();
                let duration = chained.duration;

                if (loops > 0 && isFinite(loops)) {
                    duration = loops * duration;
                }

                this.addTweenTimeObjects(chained, time + duration, tweenObjects);
            }
        } else {
            tweenObjects.push({
                tween,
                time
            });
        }
    }

    private addTweenCallbacks(tween: Tween): void {
        if (tween.object) {
            tween.object.visible = false;
            tween.onStartCalled(() => {
                tween.object.visible = true;
            });
            tween.onComplete(() => {
                tween.object.visible = false;
                this._onTweenComplete && this._onTweenComplete(tween, tween.object);
            });
        }

        for (const chainedTween of tween.chainedTweens) {
            this.addTweenCallbacks(chainedTween);
        }
    }

    private onLatestTweenCompleted(tween: Tween): void {
        if (!this._loop) {
            tween.object.visible = false;

            if (this._onComplete) {
                this._onComplete();
            }

            this.stop();
        }
    }

    [AUTOMATION_META](): AutomationMeta {
        return {
            type: 'animation',
            loop: this._loop,
            isPlaying: this._isPlaying
        };
    }
}
